import React, { useEffect, useMemo, useState } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { MotifProgressLoader } from "@ey-xd/motif-react";
import reportListEmptyImg from '../../assets/images/empty_product_list_default.svg';
import ErrorOrderPage from '../../components/ErrorOrderPage/ErrorOrderPage';

interface PowerBIReportProps {
  reportId: string;
  embedUrl: string;
  accessToken: string;
  fetchingToken: boolean;
  error: string | null;
  onRefresh: () => void;
}

const PowerBIReport: React.FC<PowerBIReportProps> = ({
  reportId,
  embedUrl,
  accessToken,
  fetchingToken,
  error,
  onRefresh,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeHidden, setIframeHidden] = useState(true);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);

  const hasValidCreds = !!(embedUrl && accessToken && reportId);

  const handleRefresh = () => {
    setEmbeddingError(null);
    setIsLoading(true);
    setIframeHidden(true);
    onRefresh();
  };

  useEffect(() => {
    if (error) {
      setIsLoading(false);
      setIframeHidden(true);
    }
  }, [error]);

  useEffect(() => {
    if (embeddingError) {
      setIsLoading(false);
      setIframeHidden(true);
    }
  }, [embeddingError]);

  useEffect(() => {
    const creds = !!(embedUrl && accessToken && reportId);
    if (!fetchingToken && !creds) {
      setIsLoading(false);
      setIframeHidden(true);
    }
  }, [fetchingToken, embedUrl, accessToken, reportId]);

  useEffect(() => {
    if (hasValidCreds && !fetchingToken) {
      setIsLoading(true);
      setIframeHidden(true);
    }
  }, [hasValidCreds, fetchingToken]);

  const readPowerBIError = (event: any): string => {
    const detail = event?.detail ?? event;
    if (typeof detail === "string") return detail;
    if (detail?.detailedMessage) return detail.detailedMessage;
    if (detail?.error?.message) return detail.error.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return "Error loading Power BI report.";
    }
  };

  const embedConfig = useMemo(() => {
    if (!hasValidCreds) return undefined;

    return {
      type: "report",
      id: reportId,
      embedUrl,
      accessToken,
      tokenType: models.TokenType.Embed,
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: true,
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToWidth,
        },
      },
    };
  }, [hasValidCreds, reportId, embedUrl, accessToken]);

  const eventHandlers = useMemo(
    () =>
      new Map<string, (event?: any) => void>([
        [
          "rendered",
          () => {
            setIsLoading(false);
            setIframeHidden(false);
          },
        ],
        [
          "error",
          (event: any) => {
            const msg = readPowerBIError(event);
            setEmbeddingError(msg);
            setIsLoading(false);
            setIframeHidden(true);
          },
        ],
        [
          "tokenExpired",
          () => {
            handleRefresh();
          },
        ],
      ]),
    [onRefresh]
  );

  return (
    <div className="pib-report-container">
      {(embeddingError || error) && (
        <div className="report-empty-list">
          <ErrorOrderPage
            image={reportListEmptyImg}
            header={embeddingError || error || "Error"}
            handleRefresh={handleRefresh}
          />
        </div>
      )}

      {(!embeddingError && !error && (fetchingToken || isLoading)) && (
        <MotifProgressLoader
          aria-busy="true"
          aria-label="This page is loading"
          aria-live="polite"
          show
          variant="default"
          className="report-progress"
        />
      )}

      {hasValidCreds && !fetchingToken && !error && !embeddingError && (
        <PowerBIEmbed
          embedConfig={embedConfig!}
          eventHandlers={eventHandlers}
          cssClassName={`report-style-class ${iframeHidden ? "report-hidden" : ""}`}
        />
      )}
    </div>
  );
};

export default PowerBIReport;